import { z } from 'zod';
import { UuidSchema } from './primitives/uuid.js';
import { TimelinePageResponseSchema } from './timeline.js';
import toArray from './utils/arrayish.js';

const DIGIT_ONLY_PATTERN = /^\d+$/;

const X_ACTIVITY_EVENT_TYPES = [
  'profile_bio_update',
  'profile_picture_update',
  'profile_banner_update',
  'profile_geo_update',
  'profile_url_update',
  'profile_screenname_update',
  'account_activity_generic',
  'account_activity_tweet_create',
  'account_activity_tweet_delete',
  'account_activity_favorite',
  'account_activity_unfavorite',
  'account_activity_follow',
  'account_activity_unfollow',
  'account_activity_block',
  'account_activity_unblock',
  'account_activity_mute',
  'account_activity_unmute',
  'account_activity_quote',
  'account_activity_reply',
  'account_activity_retweet',
  'account_activity_direct_message',
  'account_activity_direct_message_typing',
  'account_activity_direct_message_read',
  'account_activity_list_created',
  'account_activity_list_updated',
  'account_activity_list_deleted',
  'account_activity_list_member_added',
  'account_activity_list_member_removed',
  'account_activity_user_event',
  'account_activity_community_tweet_create',
  'account_activity_community_tweet_delete',
] as const;

const X_ACTIVITY_ACCOUNT_STATUSES = ['pending', 'active', 'errored', 'removed'] as const;
const X_ACTIVITY_SUBSCRIPTION_STATUSES = ['pending', 'valid', 'errored', 'disabled'] as const;
const X_ACTIVITY_ENVIRONMENTS = ['dev', 'prod', 'staging'] as const;

export const XActivityEventTypeSchema = z.enum(X_ACTIVITY_EVENT_TYPES);
export type XActivityEventType = z.infer<typeof XActivityEventTypeSchema>;

export const XActivityAccountStatusSchema = z.enum(X_ACTIVITY_ACCOUNT_STATUSES);
export type XActivityAccountStatus = z.infer<typeof XActivityAccountStatusSchema>;

export const XActivitySubscriptionStatusSchema = z.enum(X_ACTIVITY_SUBSCRIPTION_STATUSES);
export type XActivitySubscriptionStatus = z.infer<typeof XActivitySubscriptionStatusSchema>;

export const XActivityEnvironmentSchema = z.enum(X_ACTIVITY_ENVIRONMENTS);
export type XActivityEnvironment = z.infer<typeof XActivityEnvironmentSchema>;

const changeValueSchema = z
  .object({
    from: z.string().optional().nullable(),
    to: z.string().optional().nullable(),
  })
  .transform((value) => ({
    from: value.from ?? null,
    to: value.to ?? null,
  }));

const profileChangesSchema = z
  .object({
    bio: changeValueSchema.optional(),
    description: changeValueSchema.optional(),
    url: changeValueSchema.optional(),
    website: changeValueSchema.optional(),
    geo: changeValueSchema.optional(),
    location: changeValueSchema.optional(),
    picture: changeValueSchema.optional(),
    profile_image_url: changeValueSchema.optional(),
    banner: changeValueSchema.optional(),
    profile_banner_url: changeValueSchema.optional(),
    screenname: changeValueSchema.optional(),
    screen_name: changeValueSchema.optional(),
    username: changeValueSchema.optional(),
  })
  .passthrough();

const webhookUserSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().optional().nullable(),
    username: z.string().optional().nullable(),
    profile_image_url: z.string().optional().nullable(),
    profile_banner_url: z.string().optional().nullable(),
    profile_changes: profileChangesSchema.optional(),
  })
  .passthrough();

export type XActivityWebhookUserRaw = z.infer<typeof webhookUserSchema>;

const PROFILE_CHANGE_EVENT_MAP: Record<
  string,
  { canonicalKey: 'bio' | 'picture' | 'banner' | 'geo' | 'url' | 'screenname'; eventType: XActivityEventType }
> = {
  bio: { canonicalKey: 'bio', eventType: 'profile_bio_update' },
  description: { canonicalKey: 'bio', eventType: 'profile_bio_update' },
  url: { canonicalKey: 'url', eventType: 'profile_url_update' },
  website: { canonicalKey: 'url', eventType: 'profile_url_update' },
  geo: { canonicalKey: 'geo', eventType: 'profile_geo_update' },
  location: { canonicalKey: 'geo', eventType: 'profile_geo_update' },
  picture: { canonicalKey: 'picture', eventType: 'profile_picture_update' },
  profile_image_url: { canonicalKey: 'picture', eventType: 'profile_picture_update' },
  banner: { canonicalKey: 'banner', eventType: 'profile_banner_update' },
  profile_banner_url: { canonicalKey: 'banner', eventType: 'profile_banner_update' },
  screenname: { canonicalKey: 'screenname', eventType: 'profile_screenname_update' },
  screen_name: { canonicalKey: 'screenname', eventType: 'profile_screenname_update' },
  username: { canonicalKey: 'screenname', eventType: 'profile_screenname_update' },
};

type AccountActivityEventMapValue = {
  eventType: XActivityEventType;
};

// Account Activity API nests different event families under descriptive keys; lift them into
// explicit event types so downstream consumers can rely on a single normalized shape.
const ACCOUNT_ACTIVITY_EVENT_MAP: Record<string, AccountActivityEventMapValue> = {
  tweet_create_events: { eventType: 'account_activity_tweet_create' },
  tweet_delete_events: { eventType: 'account_activity_tweet_delete' },
  favorite_events: { eventType: 'account_activity_favorite' },
  unfavorite_events: { eventType: 'account_activity_unfavorite' },
  follow_events: { eventType: 'account_activity_follow' },
  unfollow_events: { eventType: 'account_activity_unfollow' },
  block_events: { eventType: 'account_activity_block' },
  unblock_events: { eventType: 'account_activity_unblock' },
  mute_events: { eventType: 'account_activity_mute' },
  unmute_events: { eventType: 'account_activity_unmute' },
  quote_tweet_events: { eventType: 'account_activity_quote' },
  reply_events: { eventType: 'account_activity_reply' },
  retweet_events: { eventType: 'account_activity_retweet' },
  direct_message_events: { eventType: 'account_activity_direct_message' },
  direct_message_indicate_typing_events: { eventType: 'account_activity_direct_message_typing' },
  direct_message_mark_read_events: { eventType: 'account_activity_direct_message_read' },
  list_created_events: { eventType: 'account_activity_list_created' },
  list_updated_events: { eventType: 'account_activity_list_updated' },
  list_deleted_events: { eventType: 'account_activity_list_deleted' },
  list_member_added_events: { eventType: 'account_activity_list_member_added' },
  list_member_removed_events: { eventType: 'account_activity_list_member_removed' },
  user_event: { eventType: 'account_activity_user_event' },
  community_tweet_create_events: { eventType: 'account_activity_community_tweet_create' },
  community_tweet_delete_events: { eventType: 'account_activity_community_tweet_delete' },
};

export const XActivityWebhookPayloadSchema = z
  .object({
    event_time_ms: z.string().regex(/^\d+$/),
    for_user_id: z.string().min(1),
    users: z.array(webhookUserSchema).min(1).optional(),
  })
  .passthrough()
  .transform((payload) => {
    const occurredAtMs = Number(payload.event_time_ms);
    const occurredAt = Number.isFinite(occurredAtMs) ? new Date(occurredAtMs) : new Date();
    const rawUsers = toArray<z.infer<typeof webhookUserSchema>>(payload.users);

    const normalizedUsers = rawUsers.map((user) => ({
      id: user.id,
      name: user.name ?? null,
      username: user.username ?? null,
      profileImageUrl: user.profile_image_url ?? null,
      profileBannerUrl: user.profile_banner_url ?? null,
      profileChanges: user.profile_changes ?? {},
      raw: user as Record<string, unknown>,
    }));

    const profileEvents = normalizedUsers.flatMap((user) => {
      if (!user.profileChanges) {
        return [];
      }

      return Object.entries(user.profileChanges).flatMap(([key, value]) => {
        const mapping = PROFILE_CHANGE_EVENT_MAP[key];
        if (!(mapping && value) || typeof value !== 'object') {
          return [];
        }

        const { canonicalKey, eventType } = mapping;
        const change = value as { from?: string | null; to?: string | null };
        const previousValue = change.from ?? null;
        const newValue = change.to ?? null;

        return [
          {
            eventType,
            changeKey: canonicalKey,
            occurredAt,
            forUserId: payload.for_user_id,
            subjectUserId: user.id,
            subjectDisplayName: user.name ?? null,
            subjectUsername: user.username ?? null,
            previousValue,
            newValue,
            rawChange: value,
          },
        ];
      });
    });

    const accountActivityEvents = collectAccountActivityEvents(payload as Record<string, unknown>, {
      defaultOccurredAt: occurredAt,
      forUserId: payload.for_user_id,
    });

    return {
      eventTimeMs: payload.event_time_ms,
      forUserId: payload.for_user_id,
      occurredAt,
      users: normalizedUsers,
      events: [...profileEvents, ...accountActivityEvents],
      raw: payload as Record<string, unknown>,
    };
  });

export type XActivityWebhookPayload = z.infer<typeof XActivityWebhookPayloadSchema>;

export const AdminXActivitySubscriptionSchema = z.object({
  id: UuidSchema,
  xWebhookId: z.string(),
  environment: XActivityEnvironmentSchema,
  status: XActivitySubscriptionStatusSchema,
  secretVersion: z.string(),
  crcLastChallengeAt: z.date().nullable(),
  crcLastResponseAt: z.date().nullable(),
  lastError: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type AdminXActivitySubscription = z.infer<typeof AdminXActivitySubscriptionSchema>;

export const AdminXActivityAccountSchema = z.object({
  id: UuidSchema,
  subscriptionId: UuidSchema,
  xUserId: z.string(),
  screenName: z.string().nullable(),
  displayName: z.string().nullable(),
  status: XActivityAccountStatusSchema,
  lastEventAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type AdminXActivityAccount = z.infer<typeof AdminXActivityAccountSchema>;

export const AdminXActivityQuotaSchema = z.object({
  max: z.number().int().nonnegative(),
  used: z.number().int().nonnegative(),
});

export type AdminXActivityQuota = z.infer<typeof AdminXActivityQuotaSchema>;

const AdminXActivityEventAccountSchema = z.object({
  id: UuidSchema,
  xUserId: z.string(),
  screenName: z.string().nullable(),
  displayName: z.string().nullable(),
});

export const AdminXActivityEventSchema = z.object({
  id: UuidSchema,
  account: AdminXActivityEventAccountSchema,
  eventType: XActivityEventTypeSchema,
  changeKey: z.string(),
  previousValue: z.string().nullable(),
  newValue: z.string().nullable(),
  forUserId: z.string(),
  occurredAt: z.date(),
  receivedAt: z.date(),
  hash: z.string(),
});

export type AdminXActivityEvent = z.infer<typeof AdminXActivityEventSchema>;

export const AdminXActivityOverviewResponseSchema = z.object({
  quota: AdminXActivityQuotaSchema,
  subscriptions: z.array(AdminXActivitySubscriptionSchema),
  accounts: z.array(AdminXActivityAccountSchema),
  recentEvents: z.array(AdminXActivityEventSchema),
});

export type AdminXActivityOverviewResponse = z.infer<typeof AdminXActivityOverviewResponseSchema>;

export const AdminXActivityEventsQuerySchema = z.object({
  accountId: UuidSchema.optional(),
  eventType: XActivityEventTypeSchema.optional(),
  search: z.string().max(280).optional(),
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(200).default(50),
});

export type AdminXActivityEventsQuery = z.infer<typeof AdminXActivityEventsQuerySchema>;

export const AdminXActivityEventsResponseSchema = z.object({
  events: z.array(AdminXActivityEventSchema),
  pagination: TimelinePageResponseSchema.shape.pagination.extend({
    cursor: z.string().nullable(),
    limit: z.number().int().nullable().optional(),
    offset: z.number().int().nullable().optional(),
    totalCount: z.number().int().nullable().optional(),
  }),
});

export type AdminXActivityEventsResponse = z.infer<typeof AdminXActivityEventsResponseSchema>;

export const AdminXActivityRegisterAccountInputSchema = z.object({
  xUserId: z.string().min(1),
  screenName: z.string().optional(),
  displayName: z.string().optional(),
  subscriptionId: UuidSchema.optional(),
});

export type AdminXActivityRegisterAccountInput = z.infer<typeof AdminXActivityRegisterAccountInputSchema>;

export const AdminXActivityRemoveAccountInputSchema = z.object({
  accountId: UuidSchema,
});

export type AdminXActivityRemoveAccountInput = z.infer<typeof AdminXActivityRemoveAccountInputSchema>;

export const AdminXActivityWebhookVerificationSchema = z
  .object({
    crc_token: z.string().min(1),
  })
  .transform((value) => value.crc_token);

export type AdminXActivityWebhookVerification = z.infer<typeof AdminXActivityWebhookVerificationSchema>;

type NormalizedAccountActivityEvent = {
  eventType: XActivityEventType;
  changeKey: string;
  occurredAt: Date;
  forUserId: string;
  subjectUserId: string;
  subjectDisplayName: string | null;
  subjectUsername: string | null;
  previousValue: string | null;
  newValue: string | null;
  rawChange: Record<string, unknown>;
};

function collectAccountActivityEvents(
  rawPayload: AccountActivityPayload,
  context: { defaultOccurredAt: Date; forUserId: string }
): NormalizedAccountActivityEvent[] {
  const events: NormalizedAccountActivityEvent[] = [];

  for (const [key, config] of Object.entries(ACCOUNT_ACTIVITY_EVENT_MAP)) {
    const rawCollection = rawPayload[key];
    if (!Array.isArray(rawCollection)) {
      continue;
    }
    for (const rawEvent of rawCollection) {
      if (!isPlainObject(rawEvent)) {
        continue;
      }
      events.push(
        normalizeAccountActivityEvent({
          raw: rawEvent,
          changeKey: key,
          eventType: config.eventType,
          defaultOccurredAt: context.defaultOccurredAt,
          forUserId: context.forUserId,
        })
      );
    }
  }

  const genericEvents = rawPayload.events;
  if (Array.isArray(genericEvents)) {
    for (const rawEvent of genericEvents) {
      if (!isPlainObject(rawEvent)) {
        continue;
      }
      events.push(
        normalizeAccountActivityEvent({
          raw: rawEvent,
          changeKey: 'events',
          eventType: 'account_activity_generic',
          defaultOccurredAt: context.defaultOccurredAt,
          forUserId: context.forUserId,
        })
      );
    }
  }

  return events;
}

type AccountActivityPayload = Record<string, unknown> & {
  events?: unknown;
};

type AccountActivityRawEvent = Record<string, unknown> & {
  event?: unknown;
  created_timestamp?: unknown;
  timestamp_ms?: unknown;
  event_timestamp?: unknown;
  time?: unknown;
  change?: Record<string, unknown>;
  previous_value?: unknown;
  new_value?: unknown;
};

function normalizeAccountActivityEvent(options: {
  raw: AccountActivityRawEvent;
  changeKey: string;
  eventType: XActivityEventType;
  defaultOccurredAt: Date;
  forUserId: string;
}): NormalizedAccountActivityEvent {
  const { raw, changeKey, eventType, defaultOccurredAt, forUserId } = options;
  type RawEventData = { created_at?: unknown };
  type ChangeRecord = {
    from?: unknown;
    previous?: unknown;
    old_value?: unknown;
    before?: unknown;
    to?: unknown;
    new?: unknown;
    after?: unknown;
    value?: unknown;
  };

  const rawEventData: RawEventData | undefined = isPlainObject(raw.event) ? (raw.event as RawEventData) : undefined;
  const occurredAt =
    parseTimestamp(raw.created_timestamp) ??
    parseTimestamp(raw.timestamp_ms) ??
    parseTimestamp(raw.event_timestamp) ??
    parseTimestamp(raw.time) ??
    parseTimestamp(rawEventData?.created_at) ??
    defaultOccurredAt;

  const actor = pickFirstObject(raw, ['source', 'user', 'account', 'sender']) ?? pickFirstObject(raw, ['target']);

  // biome-ignore lint/complexity/useLiteralKeys: `actor` is index-signature shaped payload data.
  const subjectUserId = sanitizeString(actor?.['id_str']) ?? sanitizeString(actor?.['id']) ?? forUserId;
  const subjectUsername =
    // biome-ignore lint/complexity/useLiteralKeys: `actor` is index-signature shaped payload data.
    sanitizeString(actor?.['screen_name']) ?? sanitizeString(actor?.['username']) ?? sanitizeString(actor?.['handle']);
  // biome-ignore lint/complexity/useLiteralKeys: `actor` is index-signature shaped payload data.
  const subjectDisplayName = sanitizeString(actor?.['name']) ?? sanitizeString(actor?.['display_name']);

  const change: ChangeRecord | undefined = isPlainObject(raw.change) ? (raw.change as ChangeRecord) : undefined;

  const previousValue =
    sanitizeString(raw.previous_value) ??
    sanitizeString(change?.from ?? change?.previous ?? change?.old_value ?? change?.before) ??
    null;

  const newValue =
    sanitizeString(raw.new_value) ??
    sanitizeString(change?.to ?? change?.new ?? change?.after ?? change?.value) ??
    null;

  return {
    eventType,
    changeKey,
    occurredAt,
    forUserId,
    subjectUserId,
    subjectDisplayName,
    subjectUsername,
    previousValue,
    newValue,
    rawChange: raw,
  };
}

function pickFirstObject(record: Record<string, unknown>, keys: string[]): Record<string, unknown> | null {
  for (const key of keys) {
    const value = record[key];
    if (isPlainObject(value)) {
      return value;
    }
  }
  return null;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function sanitizeString(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  return null;
}

function parseTimestamp(value: unknown): Date | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (DIGIT_ONLY_PATTERN.test(trimmed)) {
      const asNumber = Number(trimmed);
      if (Number.isFinite(asNumber)) {
        const date = new Date(asNumber);
        return Number.isNaN(date.getTime()) ? null : date;
      }
    }
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  return null;
}
